import { useEffect, useState } from 'react';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import PageSection from '@c/listing/shared/PageSection';
import { useCreateListing } from '../_context';
import FormSelect from '@c/forms/controls/FormSelect';
import Checkbox from '@ui/Checkbox';
import useBrandOptions from '@util/hooks/useBrandOptions';
import RadioButton from '@ui/RadioButton';
import { bikeTypes, ENGINE_SIZES } from 'models/product';
import useBikes from '@util/hooks/useBikes';
import Select from '@ui/Select';
import Toggle from '@ui/Toggle';

export const ProductInfo = ({
  otherBrand,
  setOtherBrand,
  isOtherBrand,
  setIsOtherBrand,
}: {
  otherBrand: string;
  setOtherBrand: (value: string) => void;
  isOtherBrand: boolean;
  setIsOtherBrand: (value: boolean) => void;
}) => {
  const { form, product } = useCreateListing();
  const [manualEntry, setManualEntry] = useState(false);

  const { brandOptions } = useBrandOptions(
    product.ride_types.includes('Cycling')
      ? 'cycling'
      : product.category === 'Bike Parts'
      ? 'parts'
      : product.category === 'Riding Gear'
      ? 'gear'
      : undefined
  );

  const { bikeYears, bikeMakes, bikeModelsByMake } = useBikes(
    [JSON.stringify(form.watch('year')) ?? ''],
    [form.watch('make') ?? ''],
    product.ride_types as ('Dirt Bikes' | 'ATV' | 'UTV' | 'Street')[]
  );

  useEffect(() => {
    if (!product.brand) {
      const foundBrand = brandOptions.find(
        (b) =>
          product.category3?.includes(b.value) ||
          product.category2?.includes(b.value)
      );
      if (foundBrand) {
        form.setValue('brand', foundBrand.value);
      }
    }
  }, [product.category2, product.category3, product.brand, brandOptions, form]);

  return (
    <PageSection title="Product Info" className={'my-8'}>
      <div className="flex flex-col gap-x-16 gap-y-16 sm:flex-row sm:gap-y-0">
        <div className="flex w-full flex-col gap-y-8">
          <FormLabel
            value="Title"
            required
            errorMessage={form.formState.errors.title?.message}
          >
            <FormInput
              {...form.register('title', {
                required: true,
              })}
              maxLength={80}
              placeholder="AGV Valentino Rossi Turtle Edition Helmet"
            />

            <div className="mt-4 flex w-full text-brand-gray">
              <span>Max. 80 characters</span>
            </div>
          </FormLabel>

          <FormLabel
            value="Description"
            required
            errorMessage={form.formState.errors.description?.message}
          >
            <FormTextarea
              {...form.register('description', {
                required: true,
              })}
              placeholder="Really good quality helmet, never used, still in box."
            />
          </FormLabel>

          <div className="w-full sm:w-[50rem]">
            <FormLabel value="SKU">
              <FormInput {...form.register('sku')} placeholder="1234567890" />
              <span className="text-[1.4rem] text-zinc-600">
                Used for internal record keeping
              </span>
            </FormLabel>
          </div>

          <div className="w-full sm:w-[50rem]">
            <FormLabel
              value="MPN"
              errorMessage={form.formState.errors.mpn?.message}
            >
              <FormInput
                {...form.register('mpn', {
                  setValueAs: (value: string) =>
                    typeof value === 'string'
                      ? value
                          .split(',')
                          .map((mpn) => mpn.trim())
                          .filter(Boolean)
                      : value,
                })}
                placeholder="MPN1, MPN2, MPN3"
              />
              <span className="text-[1.4rem] text-zinc-600">
                Manufacturer product numbers of all items included in the
                listing, separated by commas
              </span>
            </FormLabel>
          </div>

          <div className="w-full sm:w-[50rem]">
            {product.category !== 'Dirt Bikes' && (
              <div className="flex flex-col gap-y-6">
                {!isOtherBrand && (
                  <FormLabel
                    value="Brand"
                    required
                    errorMessage={form.formState.errors.brand?.message}
                  >
                    <FormSelect
                      allowEmpty={false}
                      options={[
                        ...brandOptions,
                        {
                          label: 'Other',
                          value: 'other',
                          id: 'other',
                        },
                      ]}
                      control={form.control}
                      name={'brand'}
                      placeholder="Select Brand"
                      defaultValue={product?.brand}
                    />
                  </FormLabel>
                )}

                <div className="mt-4">
                  {form.watch('brand') === 'other' && (
                    <FormLabel value="Other Brand" required>
                      <FormInput
                        value={otherBrand}
                        onChange={(e) => {
                          setOtherBrand(e.target.value);
                        }}
                      />
                    </FormLabel>
                  )}
                </div>

                <Checkbox
                  selected={manualEntry}
                  onChange={() => {
                    setManualEntry(!manualEntry);
                    setIsOtherBrand(!isOtherBrand);
                  }}
                  label="Brand Not Listed"
                />
                {product.category === 'Bike Parts' && (
                  <Toggle
                    label="Is OEM?"
                    enabled={product.brand === 'OEM'}
                    onChange={() => {
                      if (product.brand === 'OEM') {
                        form.setValue('brand', '');
                      } else {
                        form.setValue('brand', 'OEM');
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {product.category === 'Dirt Bikes' && (
            <div className="w-full">
              <div className="flex flex-col gap-y-6">
                <FormLabel
                  value="Bike Type"
                  required
                  errorMessage={form.formState.errors.category1?.message}
                >
                  <FormSelect
                    allowEmpty={false}
                    options={bikeTypes.options?.map((bt: any) => ({
                      id: bt.value,
                      label: bt.value,
                      value: bt.value,
                    }))}
                    control={form.control}
                    name={'category1'}
                    defaultValue={product?.category1}
                  />
                </FormLabel>

                <div className="flex items-center gap-x-4">
                  <FormLabel
                    value="Year"
                    required
                    errorMessage={form.formState.errors.year?.message}
                  >
                    <FormSelect
                      allowEmpty={false}
                      options={bikeYears.map((year) => ({
                        label: `${year}`,
                        value: year,
                        id: `${year}`,
                      }))}
                      control={form.control}
                      name={'year'}
                      defaultValue={product?.year}
                    />
                  </FormLabel>

                  <FormLabel value="Make">
                    <Select
                      allowEmpty={false}
                      options={bikeMakes.map((make) => ({
                        label: make,
                        value: make,
                        id: make,
                      }))}
                      onChange={(option: any) => {
                        form.setValue('make', option.value);
                      }}
                      value={form.watch('make')}
                    />
                  </FormLabel>
                </div>
                <FormLabel value="Model" required>
                  <Select
                    allowEmpty={false}
                    options={
                      bikeModelsByMake[
                        form.watch('make') as keyof typeof bikeModelsByMake
                      ]?.map(({ model }) => ({
                        label: model,
                        value: model,
                        id: model,
                      })) ?? [
                        {
                          label: 'Select year & make first',
                          value: '',
                          id: 'placeholder',
                        },
                      ]
                    }
                    onChange={(option: any) => {
                      form.setValue('model', option.value);
                    }}
                    value={form.watch('model')}
                    disabled={
                      !Boolean(form.watch('year')) &&
                      !Boolean(form.watch('make'))
                    }
                  />
                </FormLabel>

                <div className="w-full sm:w-[60rem]">
                  <FormLabel value="Engine Size">
                    <Select
                      allowEmpty={false}
                      options={ENGINE_SIZES.map((size) => ({
                        label: size,
                        value: +size,
                        id: size,
                      }))}
                      onChange={(option: any) => {
                        form.setValue('engine', option.value);
                      }}
                      value={form.watch('engine')?.toString() ?? ''}
                    />
                  </FormLabel>
                </div>
              </div>
            </div>
          )}

          {manualEntry && (
            <div className="sm:w-[50rem]">
              <FormLabel value="Other Brand?" labelClasses="my-4">
                <FormInput
                  placeholder={'Brand'}
                  value={product.brand === 'na' ? 'N/A' : product.brand}
                  onChange={(e) => form.setValue('brand', e.target.value)}
                />
              </FormLabel>
            </div>
          )}

          <div className="sm:w-[50rem]">
            <FormLabel
              value="Condition"
              required
              errorMessage={
                form.formState.errors.condition
                  ? 'Please select a condition'
                  : ''
              }
            >
              <div className="mt-8 flex gap-8">
                <RadioButton
                  label="New"
                  checked={form.watch('condition') === 'New'}
                  onClick={() => form.setValue('condition', 'New')}
                />

                <RadioButton
                  label="Used"
                  checked={form.watch('condition') === 'Used'}
                  onClick={() => form.setValue('condition', 'Used')}
                />
              </div>
            </FormLabel>
          </div>

          {product.category3 === 'Frames' && (
            <div className="sm:w-[50rem]">
              <FormLabel value="Title Included?">
                <div className="mt-8 flex gap-8">
                  <RadioButton
                    label="Included"
                    checked={
                      form.watch('specs')?.['Title Included'] === 'Included'
                    }
                    onClick={() =>
                      form.setValue('specs.Title Included', 'Included')
                    }
                  />

                  <RadioButton
                    label="Not Included"
                    checked={
                      !form.watch('specs')?.['Title Included'] ||
                      form.watch('specs')?.['Title Included'] === 'Not Included'
                    }
                    onClick={() =>
                      form.setValue('specs.Title Included', 'Not Included')
                    }
                  />
                </div>
              </FormLabel>
            </div>
          )}
        </div>

        <div className="hidden flex-col gap-y-6 sm:flex">
          <h3 className="text-[1.8rem] font-semibold text-black">
            Provide complete info about the product.
          </h3>

          <p className="text-[1.5rem] text-zinc-600">
            Enhance your product description to build trust with potential
            buyers.
          </p>
        </div>
      </div>
    </PageSection>
  );
};
